/**
 * Nurse Dashboard — Phase B (Core MVP)
 *
 * Tablet-first single-page workstation for nurses. Four large tabs:
 *   1) Patients & Alerts    — patient cards, vitals, doctor instructions, vitals entry modal
 *   2) Medications          — auto-derived schedule with Administer/Missed actions
 *   3) Doctor Orders        — pending / accepted / completed queue with Accept/Complete/Escalate
 *   4) Shift Status         — live ward summary banner + ward switch toggle
 *
 * All interactive controls hit ≥ 44px tap targets and use generous spacing
 * so nurses on shared tablets can act without zooming.
 */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { nurseAPI, doctorOrdersAPI, queueAPI, wardsAPI, bedsAPI, patientsAPI, patientDashboardAPI } from '../lib/api';
import {
  Activity, AlertTriangle, BedDouble, Heart, Pill, Stethoscope, ClipboardList,
  Check, X as XIcon, Bell, RefreshCw, Thermometer, Wind, ChevronRight, Building2,
  FileText, Clipboard as ClipIcon, Siren, BookmarkPlus, Search,
  Phone, FlaskConical,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { usePromptDialog } from '../components/PromptDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShiftHandoverPanel, ChecklistsPanel, EmergencyPanel, FloatingEmergencyButton } from '../components/NursePhaseC';

// ========== HELPERS ==========
const SEVERITY_COLOR = {
  high: 'bg-red-100 text-red-700 border-red-300',
  medium: 'bg-amber-100 text-amber-700 border-amber-300',
  low: 'bg-blue-100 text-blue-700 border-blue-300',
};

const PRIORITY_COLOR = {
  stat: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  normal: 'bg-blue-500 text-white',
  low: 'bg-slate-400 text-white',
};

// Big stat tile (tablet-friendly)
const StatTile = ({ icon: Icon, label, value, color = 'bg-emerald-500', sub, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div className={cn('h-12 w-12 rounded-xl flex items-center justify-center', color)}>
        <Icon size={22} className="text-white" />
      </div>
      <p className="text-3xl font-bold text-slate-900">{value}</p>
    </div>
    <p className="text-sm font-semibold text-slate-700 mt-3">{label}</p>
    {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

// ========== VITALS ENTRY MODAL ==========
const VitalsEntryDialog = ({ open, onOpenChange, patient, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({});
  }, [open]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await nurseAPI.recordVitals({
        patient_id: patient.patient_id,
        blood_pressure_systolic: form.bp_s ? Number(form.bp_s) : null,
        blood_pressure_diastolic: form.bp_d ? Number(form.bp_d) : null,
        pulse_rate: form.pulse ? Number(form.pulse) : null,
        temperature: form.temp ? Number(form.temp) : null,
        respiratory_rate: form.resp ? Number(form.resp) : null,
        oxygen_saturation: form.spo2 ? Number(form.spo2) : null,
        blood_sugar: form.sugar ? Number(form.sugar) : null,
        weight: form.weight ? Number(form.weight) : null,
        pain_score: form.pain ? Number(form.pain) : null,
        notes: form.notes || null,
      });
      toast.success(`Vitals recorded for ${patient.patient_name}`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record vitals');
    } finally {
      setSaving(false);
    }
  };

  if (!patient) return null;
  const big = 'h-12 text-base';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl">Record Vitals — {patient.patient_name}</DialogTitle>
          <p className="text-sm text-slate-500">Bed {patient.bed_number || '—'} · UHID {patient.uhid || '—'}</p>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="col-span-2 grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Systolic BP (mmHg)</Label>
              <Input type="number" inputMode="numeric" className={big} placeholder="120" value={form.bp_s || ''} onChange={e => update('bp_s', e.target.value)} data-testid="vitals-bp-systolic" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Diastolic BP (mmHg)</Label>
              <Input type="number" inputMode="numeric" className={big} placeholder="80" value={form.bp_d || ''} onChange={e => update('bp_d', e.target.value)} data-testid="vitals-bp-diastolic" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Pulse (bpm)</Label>
            <Input type="number" inputMode="numeric" className={big} placeholder="72" value={form.pulse || ''} onChange={e => update('pulse', e.target.value)} data-testid="vitals-pulse" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Temperature (°C)</Label>
            <Input type="number" inputMode="decimal" step="0.1" className={big} placeholder="36.8" value={form.temp || ''} onChange={e => update('temp', e.target.value)} data-testid="vitals-temp" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">SpO₂ (%)</Label>
            <Input type="number" inputMode="numeric" className={big} placeholder="98" value={form.spo2 || ''} onChange={e => update('spo2', e.target.value)} data-testid="vitals-spo2" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Resp Rate (/min)</Label>
            <Input type="number" inputMode="numeric" className={big} placeholder="16" value={form.resp || ''} onChange={e => update('resp', e.target.value)} data-testid="vitals-resp" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Blood Sugar (mg/dL)</Label>
            <Input type="number" inputMode="numeric" className={big} placeholder="110" value={form.sugar || ''} onChange={e => update('sugar', e.target.value)} data-testid="vitals-sugar" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Weight (kg)</Label>
            <Input type="number" inputMode="decimal" step="0.1" className={big} placeholder="65" value={form.weight || ''} onChange={e => update('weight', e.target.value)} data-testid="vitals-weight" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Pain Score (0–10)</Label>
            <Input type="number" inputMode="numeric" min={0} max={10} className={big} placeholder="0" value={form.pain || ''} onChange={e => update('pain', e.target.value)} data-testid="vitals-pain" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
            <Textarea rows={2} value={form.notes || ''} onChange={e => update('notes', e.target.value)} placeholder="Any additional observation…" data-testid="vitals-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-12 px-6">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 h-12 px-6 text-base" data-testid="vitals-save">
            {saving ? 'Saving…' : 'Save Vitals'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ========== PATIENT CARD ==========
const PatientCard = ({ patient, alerts, onRecordVitals }) => {
  const v = patient.latest_vitals || {};
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`nurse-patient-card-${patient.patient_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-bold text-slate-900 truncate">{patient.patient_name}</h3>
            {alerts && alerts.length > 0 && (
              <Badge className="bg-red-100 text-red-700 border-red-300 text-[10px]" data-testid={`alert-badge-${patient.patient_id}`}>
                <AlertTriangle size={10} className="mr-1" /> {alerts.length} alert{alerts.length > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <p className="text-xs text-slate-500">Bed <span className="font-mono font-semibold">{patient.bed_number || '—'}</span> · UHID {patient.uhid || '—'}</p>
          <p className="text-xs text-slate-500 mt-0.5">{patient.gender || ''} {patient.age ? `· ${patient.age}y` : ''} · Dr. {patient.doctor_name}</p>
        </div>
        <Button onClick={() => onRecordVitals(patient)} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-4" data-testid={`record-vitals-${patient.patient_id}`}>
          <Activity size={16} className="mr-1.5" /> Record
        </Button>
      </div>

      {(v.recorded_at || v.pulse_rate || v.blood_pressure_systolic) && (
        <div className="grid grid-cols-4 gap-2 mt-4">
          <VitalMini icon={Heart} label="BP" value={v.blood_pressure_systolic ? `${v.blood_pressure_systolic}/${v.blood_pressure_diastolic || '?'}` : '—'} />
          <VitalMini icon={Activity} label="Pulse" value={v.pulse_rate ? `${v.pulse_rate}` : '—'} />
          <VitalMini icon={Thermometer} label="Temp" value={v.temperature ? `${v.temperature}°` : '—'} />
          <VitalMini icon={Wind} label="SpO₂" value={v.oxygen_saturation ? `${v.oxygen_saturation}%` : '—'} />
        </div>
      )}

      {alerts && alerts.length > 0 && (
        <div className="mt-3 space-y-1">
          {alerts.map((a, i) => (
            <div key={i} className={cn('inline-flex items-center text-[11px] px-2 py-1 rounded-md border mr-1', SEVERITY_COLOR[a.severity] || SEVERITY_COLOR.medium)}>
              <AlertTriangle size={11} className="mr-1" /> {a.metric}: <span className="font-bold ml-1">{a.value}</span>
            </div>
          ))}
        </div>
      )}

      {patient.diagnosis && (
        <p className="text-xs text-slate-600 mt-3 italic"><span className="font-semibold not-italic text-slate-700">Diagnosis:</span> {patient.diagnosis}</p>
      )}
    </div>
  );
};

const VitalMini = ({ icon: Icon, label, value }) => (
  <div className="bg-slate-50 rounded-lg p-2 text-center">
    <Icon size={12} className="mx-auto text-slate-400 mb-0.5" />
    <p className="text-[9px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className="text-sm font-bold text-slate-800">{value}</p>
  </div>
);

// ========== MEDICATION ROW ==========
const MedRow = ({ row, onAction, busy }) => {
  const logged = row.log_status;
  // Collapsed visual for already-logged doses — keeps the schedule scannable on a long shift
  if (logged) {
    return (
      <div
        className={cn(
          'rounded-lg border bg-white px-4 py-2.5 flex items-center gap-3 text-sm',
          logged === 'administered' ? 'border-emerald-200 bg-emerald-50/30' : 'border-red-200 bg-red-50/30',
        )}
        data-testid={`med-row-${row.patient_id}-${row.rx_id}-${row.dose_time}`}
      >
        <span className="text-base font-mono font-bold text-slate-700 w-14">{row.dose_time}</span>
        <Badge className={cn('text-[10px]', logged === 'administered' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700')}>
          {logged.toUpperCase()}
        </Badge>
        <span className="text-sm font-semibold text-slate-700 truncate flex-1">
          {row.medication_name} · {row.dosage}
          <span className="text-slate-400 font-normal ml-2">{row.patient_name} · Bed {row.bed_number || '—'}</span>
        </span>
        {row.log_remarks && <span className="text-[11px] text-slate-500 italic truncate hidden md:inline-block">📝 {row.log_remarks}</span>}
        <span className="text-[10px] text-slate-400 hidden sm:inline-block">{row.log_by || ''}</span>
      </div>
    );
  }
  return (
    <div className={cn(
      'rounded-xl border bg-white p-4 transition-all',
      row.is_overdue ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 hover:shadow-sm',
    )} data-testid={`med-row-${row.patient_id}-${row.rx_id}-${row.dose_time}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xl font-bold text-slate-900 font-mono">{row.dose_time}</span>
            <Badge className={cn('text-[10px]', row.is_overdue ? 'bg-amber-200 text-amber-800' : 'bg-slate-100 text-slate-700')}>
              {row.is_overdue ? 'OVERDUE' : 'DUE'}
            </Badge>
            {row.before_food && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Before food</Badge>}
          </div>
          <p className="text-base font-semibold text-slate-800 mt-1">{row.medication_name} <span className="text-sm font-normal text-slate-500">— {row.dosage}</span></p>
          <p className="text-xs text-slate-500 mt-0.5">{row.patient_name} · Bed {row.bed_number || '—'} · {row.frequency} · {row.route}</p>
          {row.instructions && <p className="text-xs italic text-slate-500 mt-1">{row.instructions}</p>}
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0">
          <Button onClick={() => onAction(row, 'administered')} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-4" data-testid={`med-administer-${row.rx_id}-${row.dose_time}`}>
            <Check size={16} className="mr-1.5" /> Administered
          </Button>
          <Button onClick={() => onAction(row, 'missed')} disabled={busy} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 h-11 px-4" data-testid={`med-missed-${row.rx_id}-${row.dose_time}`}>
            <XIcon size={16} className="mr-1.5" /> Missed
          </Button>
        </div>
      </div>
    </div>
  );
};

// ========== DOCTOR ORDER ROW ==========
const OrderRow = ({ order, onAccept, onComplete, onEscalate, busy }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 hover:shadow-sm transition" data-testid={`order-row-${order.id}`}>
    <div className="flex items-start gap-3">
      <Badge className={cn('text-[10px] uppercase tracking-wider', PRIORITY_COLOR[order.priority] || PRIORITY_COLOR.normal)}>
        {order.priority}
      </Badge>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-800 capitalize">{order.order_type?.replace('_', ' ')}</p>
        <p className="text-sm text-slate-700 mt-0.5">{order.instructions || '(no instructions)'}</p>
        <p className="text-[11px] text-slate-400 mt-1">From Dr. {order.doctor_name} · {order.created_at?.slice(11, 16)} {order.created_at?.slice(0, 10)}</p>
        {order.accepted_by_name && <p className="text-[11px] text-blue-600 mt-0.5">Accepted by {order.accepted_by_name}</p>}
        {order.completed_by_name && <p className="text-[11px] text-emerald-600 mt-0.5">Completed by {order.completed_by_name}{order.completion_notes ? ` — ${order.completion_notes}` : ''}</p>}
        {order.escalated_by_name && <p className="text-[11px] text-red-600 mt-0.5">Escalated by {order.escalated_by_name}{order.escalation_reason ? ` — ${order.escalation_reason}` : ''}</p>}
      </div>
      <div className="flex flex-col gap-2 flex-shrink-0">
        {order.status === 'new' && (
          <Button onClick={() => onAccept(order)} disabled={busy} className="bg-blue-600 hover:bg-blue-700 h-10 px-3 text-xs" data-testid={`order-accept-${order.id}`}>Accept</Button>
        )}
        {(order.status === 'new' || order.status === 'accepted') && (
          <Button onClick={() => onComplete(order)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 h-10 px-3 text-xs" data-testid={`order-complete-${order.id}`}>Complete</Button>
        )}
        {order.status !== 'completed' && order.status !== 'escalated' && (
          <Button onClick={() => onEscalate(order)} disabled={busy} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 h-10 px-3 text-xs" data-testid={`order-escalate-${order.id}`}>Escalate</Button>
        )}
        <Badge className={cn('text-[10px]',
          order.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
          order.status === 'escalated' ? 'bg-red-100 text-red-700' :
          order.status === 'accepted' ? 'bg-blue-100 text-blue-700' :
          'bg-slate-100 text-slate-700')}>{order.status}</Badge>
      </div>
    </div>
  </div>
);

// ========== TABS ==========
const TABS = [
  { id: 'patients', label: 'Patients & Alerts', icon: Stethoscope },
  { id: 'medications', label: 'Medications', icon: Pill },
  { id: 'orders', label: 'Doctor Orders', icon: ClipboardList },
  { id: 'checklists', label: 'Checklists', icon: ClipIcon },
  { id: 'handover', label: 'Shift Handover', icon: FileText },
  { id: 'emergency', label: 'Emergency', icon: Siren },
];

// ========== ORDERS TAB (Active vs History split) ==========
const OrdersTab = ({ orders, busy, onAccept, onComplete, onEscalate }) => {
  const [view, setView] = useState('active'); // 'active' | 'history'
  const active = orders.filter(o => ['new', 'accepted'].includes(o.status));
  const history = orders.filter(o => !['new', 'accepted'].includes(o.status));
  const list = view === 'active' ? active : history;
  return (
    <div className="space-y-3" data-testid="tab-orders">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setView('active')}
          className={cn('px-4 py-2 rounded-lg text-sm font-semibold border transition', view === 'active' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50')}
          data-testid="orders-view-active"
        >
          Active ({active.length})
        </button>
        <button
          onClick={() => setView('history')}
          className={cn('px-4 py-2 rounded-lg text-sm font-semibold border transition', view === 'history' ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50')}
          data-testid="orders-view-history"
        >
          History ({history.length})
        </button>
      </div>
      {list.length > 0 ? list.map(o => (
        <OrderRow key={o.id} order={o} busy={busy} onAccept={() => onAccept(o)} onComplete={() => onComplete(o)} onEscalate={() => onEscalate(o)} />
      )) : (
        <div className="text-center py-16 text-slate-400">
          <ClipboardList size={40} className="mx-auto mb-3 opacity-40" />
          <p>{view === 'active' ? 'No active orders — you\'re all caught up.' : 'No completed or escalated orders yet.'}</p>
        </div>
      )}
    </div>
  );
};

// ========== v38.5 — Selected Patient IPD Panel ==========
// Inline IPD summary shown when the nurse selects a patient from the global header search.
// Visually mirrors the Patient Dashboard search-expand pattern: latest vitals, vitals trend,
// admission/bed info, active prescriptions (current meds), recent diagnoses, latest labs.
const SelectedPatientPanel = ({ payload, onClose, onRecordVitals }) => {
  // patient_dashboard.detail returns a wrapper:
  //   { patient: {...demo+name+age}, latest_vitals, vitals_history, prescriptions[],
  //     diagnoses[], lab_reports[], assigned_doctors[], admission_history[] ... }
  const p = payload?.patient || {};
  const latestV = payload?.latest_vitals || {};
  const vitalsHistory = payload?.vitals_history || [];
  const dx = (payload?.diagnoses || []).slice(0, 5);
  const labs = (payload?.lab_reports || []).slice(0, 5);
  const docs = payload?.assigned_doctors || [];

  // Flatten the latest 2 prescriptions' nested medications list into a single array of
  // displayable line-items (each prescription doc carries an array under `medications`).
  const activeMeds = (() => {
    const out = [];
    (payload?.prescriptions || []).filter(r => r.is_active !== false).slice(0, 3).forEach(rx => {
      (rx.medications || []).forEach(m => {
        out.push({
          name: m.name || m.medication_name || '—',
          dosage: m.dosage || '',
          frequency: m.frequency || '',
          route: m.route || '',
          doctor_name: rx.doctor_name || '',
        });
      });
    });
    return out.slice(0, 6);
  })();

  // Pick the open admission (no discharge_date) for current bed/ward info.
  const admission = (payload?.admission_history || []).find(a => !a.discharge_date) || (payload?.admission_history || [])[0] || null;

  const heartRate = latestV.heart_rate ?? latestV.pulse_rate;
  const spo2      = latestV.spo2 ?? latestV.oxygen_saturation;
  const patientName = p.name || `${p.first_name||''} ${p.last_name||''}`.trim() || '—';

  return (
    <div className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/30 to-white p-5 shadow-md space-y-4" data-testid="nurse-selected-patient-panel">
      {/* Patient header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-lg font-bold">
            {patientName.charAt(0).toUpperCase()}
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900" data-testid="nurse-selected-patient-name">{patientName}</h2>
            <p className="text-xs text-slate-500">
              UHID <span className="font-mono font-semibold text-slate-700">{p.patient_id || '—'}</span>
              {p.age ? ` · ${p.age}y` : ''} {p.gender ? `· ${p.gender}` : ''}
              {p.blood_group ? ` · ${p.blood_group}` : ''}
            </p>
            {p.phone && <p className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5"><Phone size={10} /> {p.phone}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => onRecordVitals({
              patient_id: p.id, patient_name: patientName, uhid: p.patient_id,
              bed_number: admission?.bed_number,
            })}
            className="bg-emerald-600 hover:bg-emerald-700 h-10"
            data-testid="nurse-selected-record-vitals"
          >
            <Activity size={14} className="mr-1.5" /> Record Vitals
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-10 w-10" data-testid="nurse-selected-close">
            <XIcon size={16} />
          </Button>
        </div>
      </div>

      {/* IPD-related quick facts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <PanelStat icon={BedDouble}   label="Bed"             value={admission?.bed_number || '—'} sub={admission?.ward_name || (admission ? 'Admitted' : 'Not admitted')} />
        <PanelStat icon={Stethoscope} label="Treating Doctor" value={docs[0]?.name ? (docs[0].name.startsWith('Dr.') ? docs[0].name : `Dr. ${docs[0].name}`) : '—'} sub={docs[0]?.department || docs[0]?.specialization || ''} />
        <PanelStat icon={ClipboardList} label="Active Diagnoses" value={dx.length || '0'} sub={(dx[0]?.diagnosis_name || dx[0]?.diagnosis_text || dx[0]?.text || '')?.slice(0, 28)} />
        <PanelStat icon={Pill}        label="Active Meds"     value={activeMeds.length || '0'} sub={activeMeds[0]?.name || ''} />
      </div>

      {/* Latest vitals */}
      {(heartRate || latestV.blood_pressure_systolic || latestV.temperature || spo2) && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Activity size={12} /> Latest Vitals
            {latestV.recorded_at && <span className="font-normal normal-case text-slate-400 ml-1">· {new Date(latestV.recorded_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>}
          </p>
          <div className="grid grid-cols-4 gap-2">
            <VitalMini icon={Heart}       label="BP"    value={latestV.blood_pressure_systolic ? `${latestV.blood_pressure_systolic}/${latestV.blood_pressure_diastolic ?? '?'}` : '—'} />
            <VitalMini icon={Activity}    label="Pulse" value={heartRate ?? '—'} />
            <VitalMini icon={Thermometer} label="Temp"  value={latestV.temperature ? `${latestV.temperature}°` : '—'} />
            <VitalMini icon={Wind}        label="SpO₂"  value={spo2 != null ? `${spo2}%` : '—'} />
          </div>
        </div>
      )}

      {/* Vitals history table */}
      {vitalsHistory.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <ClipIcon size={12} /> Vitals History · last {Math.min(vitalsHistory.length, 8)}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-2 py-1.5 text-left text-slate-500">When</th>
                  <th className="px-2 py-1.5 text-left text-slate-500">BP</th>
                  <th className="px-2 py-1.5 text-left text-slate-500">Pulse</th>
                  <th className="px-2 py-1.5 text-left text-slate-500">Temp</th>
                  <th className="px-2 py-1.5 text-left text-slate-500">SpO₂</th>
                  <th className="px-2 py-1.5 text-left text-slate-500">By</th>
                </tr>
              </thead>
              <tbody>
                {vitalsHistory.slice(0, 8).map((v, i) => (
                  <tr key={v.id || i} className="border-t border-slate-100" data-testid={`nurse-vitals-history-row-${i}`}>
                    <td className="px-2 py-1.5 text-slate-500">{v.recorded_at ? new Date(v.recorded_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                    <td className="px-2 py-1.5">{v.blood_pressure_systolic ? `${v.blood_pressure_systolic}/${v.blood_pressure_diastolic ?? '?'}` : '—'}</td>
                    <td className="px-2 py-1.5">{v.heart_rate ?? v.pulse_rate ?? '—'}</td>
                    <td className="px-2 py-1.5">{v.temperature ?? '—'}</td>
                    <td className="px-2 py-1.5">{v.spo2 ?? v.oxygen_saturation ?? '—'}</td>
                    <td className="px-2 py-1.5 text-slate-400">{v.recorded_by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Two-column: current meds + recent diagnoses + labs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Current meds */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Pill size={12} /> Active Medications</p>
          {activeMeds.length === 0 ? <p className="text-xs text-slate-400">No active prescriptions.</p> : (
            <ul className="space-y-1.5">
              {activeMeds.map((m, i) => (
                <li key={i} className="text-xs border-l-2 border-emerald-400 pl-2" data-testid={`nurse-selected-rx-${i}`}>
                  <p className="font-semibold text-slate-700">{m.name} {m.dosage && <span className="text-slate-400 font-normal">— {m.dosage}</span>}</p>
                  <p className="text-[10px] text-slate-400">{m.frequency || ''} {m.route ? `· ${m.route}` : ''} {m.doctor_name ? `· ${m.doctor_name.startsWith('Dr.') ? m.doctor_name : `Dr. ${m.doctor_name}`}` : ''}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Diagnoses + labs */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><ClipboardList size={12} /> Recent Diagnoses</p>
            {dx.length === 0 ? <p className="text-xs text-slate-400">No diagnoses on record.</p> : (
              <ul className="space-y-1">
                {dx.map((d, i) => (
                  <li key={d.id || i} className="text-xs" data-testid={`nurse-selected-dx-${i}`}>
                    <span className="font-semibold text-slate-700">{d.diagnosis_name || d.diagnosis_text || d.text || '—'}</span>
                    {d.diagnosis_code && <span className="text-[10px] text-slate-500 font-mono ml-1">({d.diagnosis_code})</span>}
                    {d.severity && <span className="text-[10px] text-amber-700 ml-1">· {d.severity}</span>}
                    {d.doctor_name && <span className="text-[10px] text-slate-400 ml-1">— {d.doctor_name.startsWith('Dr.') ? d.doctor_name : `Dr. ${d.doctor_name}`}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><FlaskConical size={12} /> Recent Lab Reports</p>
            {labs.length === 0 ? <p className="text-xs text-slate-400">No lab reports yet.</p> : (
              <ul className="space-y-1">
                {labs.map((l, i) => (
                  <li key={l.id || i} className="text-xs" data-testid={`nurse-selected-lab-${i}`}>
                    <span className="font-semibold text-slate-700">{l.test_name || l.name || '—'}</span>
                    {l.created_at && <span className="text-[10px] text-slate-400 ml-1">· {new Date(l.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PanelStat = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 mb-1">
      <Icon size={11} /> {label}
    </div>
    <p className="text-lg font-bold text-slate-800 leading-tight">{value}</p>
    {sub && <p className="text-[10px] text-slate-400 mt-0.5 truncate">{sub}</p>}
  </div>
);

// ========== MAIN PAGE ==========
const NurseDashboardPage = () => {
  const { user, hasPermission } = useAuth();
  const [tab, setTab] = useState('patients');
  const [allWards, setAllWards] = useState(false);
  const [data, setData] = useState(null);
  const [meds, setMeds] = useState([]);
  const [orders, setOrders] = useState([]);
  const [doctorsForAlert, setDoctorsForAlert] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vitalsPatient, setVitalsPatient] = useState(null);
  const [busy, setBusy] = useState(false);
  const [promptNode, openPrompt] = usePromptDialog();
  const [reserveOpen, setReserveOpen] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');

  // v38.5 — Hospital-wide patient search (header). Selecting a patient opens an
  // inline IPD summary panel between the header and the KPI tiles, mirroring
  // the search/expand pattern from the Patient Dashboard.
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalResults, setGlobalResults] = useState([]);
  const [globalSearching, setGlobalSearching] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const globalSearchRef = useRef(null);

  const handleGlobalSearch = async (q) => {
    setGlobalSearch(q);
    if (q.trim().length < 2) { setGlobalResults([]); return; }
    setGlobalSearching(true);
    try {
      const res = await patientDashboardAPI.search(q.trim());
      setGlobalResults(res.data || []);
    } catch { /* ignore */ }
    setGlobalSearching(false);
  };

  const handleSelectGlobalPatient = async (p) => {
    setGlobalResults([]); setGlobalSearch('');
    setSelectedLoading(true);
    try {
      const res = await patientDashboardAPI.detail(p.id);
      setSelectedPatient(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load patient details');
    } finally {
      setSelectedLoading(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (globalSearchRef.current && !globalSearchRef.current.contains(e.target)) {
        setGlobalResults([]);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, m, o, doc] = await Promise.all([
        nurseAPI.dashboard(allWards),
        nurseAPI.medications(allWards),
        doctorOrdersAPI.list({ all_wards: allWards }),
        queueAPI.doctorsToday().catch(() => ({ data: [] })),
      ]);
      setData(d.data);
      setMeds(m.data || []);
      setOrders(o.data || []);
      const docs = (doc.data || []).map(x => ({ id: x.user_id || x.id, name: x.name || `${x.first_name || ''} ${x.last_name || ''}`.trim() || 'Doctor' }));
      setDoctorsForAlert(docs);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load nurse dashboard');
    } finally {
      setLoading(false);
    }
  }, [allWards]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s — critical info should stay live
  useEffect(() => {
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const alertsByPid = useMemo(() => {
    const map = {};
    (data?.critical_alerts || []).forEach(a => { map[a.patient_id] = a.alerts; });
    return map;
  }, [data]);

  const handleMedAction = async (row, status) => {
    let remarks = '';
    if (status === 'missed') {
      remarks = await openPrompt({
        title: `Why was ${row.medication_name} missed?`,
        presets: ['Patient sleeping', 'NPO / before procedure', 'Patient refused', 'Drug not available', 'BP/Pulse low — held'],
        placeholder: 'Additional notes…',
        confirmLabel: 'Log Missed',
        destructive: true,
      });
      if (!remarks) return;
    }
    setBusy(true);
    try {
      await nurseAPI.administer({
        patient_id: row.patient_id,
        rx_id: row.rx_id,
        medication_name: row.medication_name,
        dosage: row.dosage,
        dose_time: row.dose_time,
        date: row.date,
        status,
        remarks,
      });
      toast.success(`${status === 'administered' ? 'Administered' : 'Logged as missed'} — ${row.medication_name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to log medication');
    } finally {
      setBusy(false);
    }
  };

  const handleOrderAction = async (action, order, extra) => {
    setBusy(true);
    try {
      if (action === 'accept') await doctorOrdersAPI.accept(order.id);
      else if (action === 'complete') await doctorOrdersAPI.complete(order.id, extra);
      else if (action === 'escalate') await doctorOrdersAPI.escalate(order.id, extra);
      toast.success(`Order ${action}ed`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action} order`);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="space-y-6" data-testid="nurse-dashboard-loading">
        <div className="h-10 w-72 bg-slate-200 rounded animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-slate-200 rounded-2xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (!data) return <p className="text-center py-16 text-slate-500">No data</p>;

  return (
    <div className="space-y-6" data-testid="nurse-dashboard">
      {/* ===== Header / Shift banner ===== */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Nurse Workstation</h1>
          <p className="text-sm text-slate-500 mt-1">
            Welcome, <span className="font-semibold">{user?.first_name}</span> · Shift live since {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>

        {/* v38.5 — Hospital-wide Patient Search */}
        <div className="relative w-full sm:w-96" ref={globalSearchRef}>
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search any patient by name, UHID, phone…"
            value={globalSearch}
            onChange={(e) => handleGlobalSearch(e.target.value)}
            className="pl-9 h-11 border-slate-300"
            data-testid="nurse-global-patient-search"
          />
          {globalSearch.trim().length >= 2 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-72 overflow-y-auto" data-testid="nurse-global-search-dropdown">
              {globalSearching && (
                <div className="p-3 text-center text-xs text-slate-400">Searching…</div>
              )}
              {!globalSearching && globalResults.length === 0 && (
                <div className="p-3 text-center text-xs text-slate-400">No patients match "{globalSearch}"</div>
              )}
              {globalResults.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handleSelectGlobalPatient(p)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-left border-b last:border-0"
                  data-testid={`nurse-global-search-result-${p.id}`}
                >
                  <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-sm font-bold">
                    {p.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-700 truncate">{p.name}</p>
                    <p className="text-[11px] text-slate-400">{p.patient_id} · {p.age ? `${p.age}y · ` : ''}{p.gender || ''}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white">
            <Building2 size={16} className="text-slate-500" />
            <div className="text-xs">
              <p className="text-slate-400 uppercase tracking-wider text-[10px]">Ward</p>
              <p className="font-semibold text-slate-700">{data.assigned_ward?.name || (allWards ? 'All Wards' : 'Not Assigned')}</p>
            </div>
          </div>
          <Button
            onClick={() => setAllWards(v => !v)}
            variant={allWards ? 'default' : 'outline'}
            className={cn('h-11 px-4', allWards ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'border-slate-300')}
            data-testid="all-wards-toggle"
          >
            <Bell size={16} className="mr-1.5" /> {allWards ? 'All Wards' : 'Emergency: All Wards'}
          </Button>
          <Button onClick={load} variant="outline" size="icon" className="h-11 w-11" data-testid="nurse-refresh">
            <RefreshCw size={16} className={cn(loading && 'animate-spin')} />
          </Button>
          {(user?.is_super_admin || hasPermission?.('beds.temp_reserve')) && (
            <Button
              onClick={() => setReserveOpen(true)}
              className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="temp-reserve-bed-btn"
              title="Temporarily reserve a bed for an incoming patient — Front Desk/Billing finalises the admission later"
            >
              <BookmarkPlus size={16} className="mr-1.5" /> Reserve Bed
            </Button>
          )}
        </div>
      </div>

      {/* v38.5 — Inline IPD summary for the searched patient */}
      {selectedLoading && (
        <div className="rounded-2xl border-2 border-emerald-200 bg-white p-6 animate-pulse" data-testid="nurse-patient-panel-loading">
          <p className="text-sm text-slate-400">Loading patient IPD summary…</p>
        </div>
      )}
      {selectedPatient && !selectedLoading && (
        <SelectedPatientPanel
          payload={selectedPatient}
          onClose={() => setSelectedPatient(null)}
          onRecordVitals={(p) => setVitalsPatient(p)}
        />
      )}

      {/* ===== KPI tiles ===== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile icon={BedDouble} label="Assigned Patients" value={data.stats?.patient_count} color="bg-blue-500" sub={data.assigned_ward?.name || (allWards ? 'All wards' : '—')} testId="stat-patients" />
        <StatTile icon={AlertTriangle} label="Critical Alerts" value={data.stats?.alert_count} color="bg-red-500" sub="Out-of-range vitals" testId="stat-alerts" />
        <StatTile icon={Pill} label="Due Medications" value={data.stats?.due_med_count} color="bg-amber-500" sub="Next 6 hours" testId="stat-meds" />
        <StatTile icon={ClipboardList} label="Pending Orders" value={data.stats?.pending_order_count} color="bg-purple-500" sub="From doctors" testId="stat-orders" />
      </div>

      {/* ===== Tab strip ===== */}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto" data-testid="nurse-tabs">
        {TABS.map(t => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                'flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-all border-b-2',
                active ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50',
              )}
              data-testid={`nurse-tab-${t.id}`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ===== Tab content ===== */}
      {tab === 'patients' && (
        <div className="space-y-6" data-testid="tab-patients">
          {data.critical_alerts && data.critical_alerts.length > 0 && (
            <div className="rounded-2xl border-2 border-red-300 bg-red-50 p-4">
              <p className="text-sm font-bold text-red-700 mb-2 flex items-center gap-1.5"><AlertTriangle size={16} /> Critical Alerts</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {data.critical_alerts.map((a, i) => (
                  <div key={i} className="rounded-lg bg-white border border-red-200 p-3" data-testid={`critical-alert-${a.patient_id}`}>
                    <p className="text-sm font-semibold text-slate-800">{a.patient_name} <span className="text-xs text-slate-500">· Bed {a.bed}</span></p>
                    <div className="mt-1 space-y-0.5">
                      {a.alerts.map((al, j) => (
                        <div key={j} className={cn('inline-flex items-center text-[11px] px-2 py-0.5 rounded mr-1 border', SEVERITY_COLOR[al.severity] || SEVERITY_COLOR.medium)}>{al.metric}: <span className="font-bold ml-1">{al.value}</span></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.doctor_instructions && data.doctor_instructions.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-1.5"><Stethoscope size={14} /> Latest Doctor Instructions</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {data.doctor_instructions.map((i, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-2 rounded bg-slate-50">
                    <ChevronRight size={14} className="text-slate-400 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700"><span className="font-semibold">{i.patient_name}</span> — {i.instructions}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{i.doctor_name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm font-bold text-slate-700">Patients ({(data.patients || []).filter(p => {
              const q = patientSearch.trim().toLowerCase();
              if (!q) return true;
              return (p.patient_name || '').toLowerCase().includes(q)
                || (p.uhid || '').toLowerCase().includes(q)
                || (p.bed_number || '').toLowerCase().includes(q)
                || (p.doctor_name || '').toLowerCase().includes(q);
            }).length})</p>
            <div className="relative w-full sm:w-72">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by name, UHID, bed, doctor"
                value={patientSearch}
                onChange={e => setPatientSearch(e.target.value)}
                className="pl-8 h-9"
                data-testid="nurse-patient-search"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(() => {
              const q = patientSearch.trim().toLowerCase();
              const filtered = q
                ? (data.patients || []).filter(p =>
                    (p.patient_name || '').toLowerCase().includes(q)
                    || (p.uhid || '').toLowerCase().includes(q)
                    || (p.bed_number || '').toLowerCase().includes(q)
                    || (p.doctor_name || '').toLowerCase().includes(q))
                : (data.patients || []);
              return filtered.length > 0 ? filtered.map(p => (
                <PatientCard key={p.patient_id} patient={p} alerts={alertsByPid[p.patient_id]} onRecordVitals={setVitalsPatient} />
              )) : (
              <div className="col-span-full text-center py-16 text-slate-400">
                <BedDouble size={40} className="mx-auto mb-3 opacity-40" />
                <p>{q ? 'No patients match your search.' : 'No admitted patients in your scope.'}</p>
                {!q && !allWards && <p className="text-xs mt-2">Toggle "Emergency: All Wards" if you're covering for another nurse.</p>}
              </div>
            );
            })()}
          </div>
        </div>
      )}

      {tab === 'medications' && (
        <div className="space-y-3" data-testid="tab-medications">
          {meds.length > 0 ? meds.map((m, i) => (
            <MedRow key={`${m.rx_id}-${m.dose_time}-${i}`} row={m} onAction={handleMedAction} busy={busy} />
          )) : (
            <div className="text-center py-16 text-slate-400">
              <Pill size={40} className="mx-auto mb-3 opacity-40" />
              <p>No medications scheduled for the patients in your scope.</p>
            </div>
          )}
        </div>
      )}

      {tab === 'orders' && (
        <OrdersTab
          orders={orders}
          busy={busy}
          onAccept={(o) => handleOrderAction('accept', o)}
          onComplete={async (o) => {
            const notes = await openPrompt({
              title: `Complete order — ${o.order_type}`,
              presets: ['Done', 'Done with notes below', 'Patient cooperated', 'Required SRN assistance'],
              placeholder: 'Optional notes',
              confirmLabel: 'Mark Complete',
              required: false,
            });
            await handleOrderAction('complete', o, notes || '');
          }}
          onEscalate={async (o) => {
            const reason = await openPrompt({
              title: `Escalate order — ${o.order_type}`,
              presets: ['Cannot perform — clarification needed', 'Patient refused', 'Equipment unavailable', 'Time critical — doctor unreachable'],
              placeholder: 'Add detail',
              confirmLabel: 'Escalate',
              destructive: true,
            });
            if (!reason) return;
            await handleOrderAction('escalate', o, reason);
          }}
        />
      )}

      {tab === 'checklists' && (
        <ChecklistsPanel patients={data.patients || []} onRefresh={load} />
      )}

      {tab === 'handover' && (
        <ShiftHandoverPanel wardId={data.assigned_ward?.id} wardName={data.assigned_ward?.name} />
      )}

      {tab === 'emergency' && (
        <EmergencyPanel
          patients={data.patients || []}
          doctorsForAlert={doctorsForAlert}
          onRefresh={load}
        />
      )}

      {tab === 'shift' && null}

      <VitalsEntryDialog
        open={!!vitalsPatient}
        onOpenChange={(v) => { if (!v) setVitalsPatient(null); }}
        patient={vitalsPatient}
        onSaved={load}
      />

      {/* Floating Code Blue button — always reachable */}
      <FloatingEmergencyButton onClick={() => setTab('emergency')} />

      <ReserveBedDialog open={reserveOpen} onOpenChange={setReserveOpen} onReserved={load} />

      {/* Shadcn-based prompt dialog (replaces window.prompt) */}
      {promptNode}
    </div>
  );
};

// ============================ ReserveBedDialog (v33) ============================
// Nurse-only "Temp Allocate Bed" — fetches free beds across all wards, lets the
// nurse pick one, type/select a patient name, and reserves it. Front Desk / Billing
// then finalises the formal admission.
const ReserveBedDialog = ({ open, onOpenChange, onReserved }) => {
  const [loadingLists, setLoadingLists] = useState(false);
  const [wards, setWards] = useState([]);
  const [beds, setBeds] = useState([]);
  const [bedId, setBedId] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [patientId, setPatientId] = useState('');
  const [patientName, setPatientName] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBedId(''); setPatientId(''); setPatientName(''); setPatientSearch(''); setPatientResults([]); setNotes('');
    setLoadingLists(true);
    Promise.all([wardsAPI.list(), bedsAPI.list({ status: 'available' })])
      .then(([w, b]) => { setWards(w.data || []); setBeds(b.data || []); })
      .catch(() => toast.error('Failed to load wards/beds'))
      .finally(() => setLoadingLists(false));
  }, [open]);

  // Patient search-as-you-type
  useEffect(() => {
    const q = patientSearch.trim();
    if (q.length < 2) { setPatientResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await patientsAPI.list({ search: q, limit: 8 });
        if (!cancelled) setPatientResults(r.data || []);
      } catch { /* ignore */ }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [patientSearch]);

  const submit = async () => {
    if (!bedId) { toast.error('Select a bed'); return; }
    if (!patientId && !patientName.trim()) { toast.error('Pick or type the patient name'); return; }
    setSaving(true);
    try {
      await bedsAPI.tempReserve(bedId, { patient_id: patientId || undefined, patient_name: patientName, notes });
      toast.success('Bed reserved — Front Desk / Billing can now finalise the admission.');
      onReserved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reserve bed');
    }
    setSaving(false);
  };

  const wardName = (id) => wards.find(w => w.id === id)?.name || '—';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="reserve-bed-dialog">
        <DialogHeader><DialogTitle>Temporarily Reserve a Bed</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
            <strong>For emergencies and urgent walk-ins.</strong> You're holding this bed for an incoming patient — Front Desk / Billing will finalise the formal admission afterwards. Payment is NOT required at this step.
          </div>

          <div>
            <Label>Free Bed *</Label>
            <Select value={bedId} onValueChange={setBedId}>
              <SelectTrigger data-testid="reserve-bed-select"><SelectValue placeholder={loadingLists ? 'Loading…' : 'Choose a free bed'} /></SelectTrigger>
              <SelectContent>
                {beds.length === 0 ? <div className="px-2 py-3 text-xs text-slate-500">No free beds right now.</div> : beds.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.bed_number} · {wardName(b.ward_id)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Patient — search registered, or type a new name</Label>
            <Input value={patientSearch} onChange={(e) => { setPatientSearch(e.target.value); setPatientId(''); setPatientName(e.target.value); }} placeholder="Start typing patient name or UHID…" data-testid="reserve-patient-search" />
            {patientResults.length > 0 && !patientId && (
              <div className="mt-1 border rounded-md bg-white shadow-sm max-h-44 overflow-y-auto text-sm">
                {patientResults.map(p => (
                  <button type="button" key={p.id}
                    onClick={() => { setPatientId(p.id); setPatientName(`${p.first_name} ${p.last_name}`); setPatientSearch(`${p.first_name} ${p.last_name}`); setPatientResults([]); }}
                    className="block w-full text-left px-3 py-1.5 hover:bg-slate-50"
                    data-testid={`reserve-patient-${p.id}`}>
                    {p.first_name} {p.last_name} <span className="text-xs text-slate-400">{p.uhid ? `· ${p.uhid}` : ''}</span>
                  </button>
                ))}
              </div>
            )}
            {patientId && <p className="text-xs text-emerald-600 mt-1">✓ Linked to registered patient {patientName}</p>}
            {!patientId && patientName && <p className="text-xs text-slate-500 mt-1">Will be reserved as an unregistered walk-in: <strong>{patientName}</strong></p>}
          </div>

          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. RTA case · came via ambulance · needs ICU follow-up" data-testid="reserve-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !bedId} className="bg-emerald-600 hover:bg-emerald-700" data-testid="reserve-submit-btn">{saving ? 'Reserving…' : 'Reserve Bed'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NurseDashboardPage;
